import { forwardRef, useImperativeHandle, useState } from 'react';
import { IconFolder } from 'components/Icons';
import { KSButton } from 'presentation/components/KSQ2/Common';
import KSInputField from 'presentation/components/KSInputField';
import { CreateModal } from '../CreateModal';

export interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string }) => void;
  locationName?: string;
  isPending?: boolean;
}

export interface CreateGroupModalRef {
  resetState: () => void;
}

export const CreateGroupModal = forwardRef<CreateGroupModalRef, CreateGroupModalProps>(
  ({ isOpen, onClose, onSubmit, locationName, isPending = false }, ref) => {
    const [title, setTitle] = useState('');

    useImperativeHandle(ref, () => ({
      resetState: () => {
        setTitle('');
      },
    }));

    const handleSubmit = () => {
      if (!title.trim() || isPending) return;
      onSubmit({ title });
      setTitle('');
    };

    const handleClose = () => {
      setTitle('');
      onClose();
    };

    return (
      <CreateModal
        isOpen={isOpen}
        onClose={handleClose}
        title="New Group"
        subtitle={locationName}
        icon={<IconFolder />}
        footer={
          <CreateModal.Actions>
            <KSButton variant="secondary" label="Cancel" onClick={handleClose} disabled={isPending} />
            <KSButton
              variant="primary"
              label={isPending ? 'Creating...' : 'Create'}
              onClick={handleSubmit}
              disabled={isPending || !title.trim()}
            />
          </CreateModal.Actions>
        }
      >
        <CreateModal.FormRow label="Title">
          <KSInputField
            value={title}
            placeholder="New group title"
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            disabled={isPending}
          />
        </CreateModal.FormRow>
      </CreateModal>
    );
  }
);

CreateGroupModal.displayName = 'CreateGroupModal';
